import { WatchQueryFetchPolicy } from '@apollo/client';
import Decimal from 'decimal.js';
import { useMemo } from 'react';

import { getKindFromProvider } from '@/modules/assetProviderIntegrations/assetProviderIntegrations.utils';
import { AssetIntegrationProviders } from '@/modules/assetProviderIntegrations/shared/constants';

import { useLifeInsuranceStepIntegrationInsurancesQuery } from '../graphql/LifeInsuranceStep.generated';
import { LifeInsuranceStepFormShape } from '../LifeInsuranceStep.types';

interface UseLifeInsuranceStepDataOpts {
  householdId: string;
  linkedHouseholdAssetProvider: AssetIntegrationProviders | null;
  /**
   * Overrides Apollo's default. Callers that can change the household's
   * integration clients in the same session need this, or the cache hands
   * them the previous selection's rows back.
   */
  fetchPolicy?: WatchQueryFetchPolicy;
}

export function useLifeInsuranceStepData({
  householdId,
  linkedHouseholdAssetProvider,
  fetchPolicy,
}: UseLifeInsuranceStepDataOpts) {
  const integrationClientKind = linkedHouseholdAssetProvider
    ? getKindFromProvider(linkedHouseholdAssetProvider)
    : undefined;

  const { data, loading } = useLifeInsuranceStepIntegrationInsurancesQuery({
    variables: {
      householdsLike: { id: householdId },
      integrationClientKind,
    },
    skip: !integrationClientKind,
    fetchPolicy,
  });

  const integrationInsurances = useMemo(() => {
    return (
      data?.integrationInsurances?.edges
        ?.map((edge) => edge?.node)
        .filter((node): node is NonNullable<typeof node> => node != null) ?? []
    );
  }, [data]);

  const defaultValues: LifeInsuranceStepFormShape = useMemo(() => {
    const policies: LifeInsuranceStepFormShape['policies'] = {};

    for (const insurance of integrationInsurances) {
      policies[insurance.id] = {
        shouldImport: false,
        integrationInsuranceId: insurance.id,
        name: insurance.name,
        policyType: '',
        ownerEntityId: '',
        deathBenefitAmount: insurance.value
          ? new Decimal(insurance.value)
          : null,
        isConvertible: false,
        conversionDate: null,
        insuranceCarrier: '',
        policyNumber: insurance.policyNumber ?? '',
        policyHolderIDs: ['', ''],
        policyStartDate: insurance.startDate
          ? new Date(insurance.startDate)
          : null,
        termLength: insurance.termDurationYears ?? '',
        premiumAmount: insurance.premiumAmount
          ? new Decimal(insurance.premiumAmount)
          : null,
        premiumFrequency: '',
        premiumInitialDueDate: null,
        cashValue: insurance.cashValue
          ? new Decimal(insurance.cashValue)
          : null,
        cashValueDate: insurance.cashValue ? new Date() : null,
        loanBalanceOutstanding: null,
        notes: '',
      };
    }

    return { policies };
  }, [integrationInsurances]);

  return { integrationInsurances, defaultValues, loading };
}
